import { HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useClickAway, useMedia } from 'react-use'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Link from 'next/link'

import Container from '@/components/base/container'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'
import { WPImage, WPPageData } from '@/types'

type ProjectsListItem = {
  title: string
  location: string
  country: string
  image?: Partial<WPImage>
  preview_images?: Partial<WPImage>[]
  slug: string
  year: string | number
  material: string
  style: string
  context: string
}

type ProjectsListData = {
  title?: string
  collection?: ProjectsListItem[]
}

type FilterKey = 'context' | 'style' | 'material' | 'country' | 'year'

type ProjectsListProps = HTMLAttributes<HTMLDivElement> & {
  pageProps: WPPageData & {
    projects_list?: ProjectsListData
  }
}

// const CONTEXT_ORDER = [
//   'Residenziale',
//   'Attico',
//   'Villa',
//   'Appartamento',
//   'Open space'
// ]
// const STYLE_ORDER = ['Essenziale', 'Materico', 'Caldo', 'Grafico']
// const MATERIAL_ORDER = ['Pietra', 'Legno', 'Gres', 'Metallo', 'Vetro']
const COUNTRY_ORDER = ['Italia', 'Francia', 'Germania', 'Svizzera']
// const YEAR_ORDER = ['2025', '2024', '2023', '2022', '2021', '2020']

const createEmptyFilters = () => ({
  context: [],
  style: [],
  material: [],
  country: [],
  year: []
})

const sortByOrder = (values: string[], order: string[]) => {
  return [...values].sort((a, b) => {
    const indexA = order.indexOf(a)
    const indexB = order.indexOf(b)

    if (indexA === -1 && indexB === -1) return a.localeCompare(b)
    if (indexA === -1) return 1
    if (indexB === -1) return -1

    return indexA - indexB
  })
}

const ProjectsList = ({
  pageProps,
  className,
  ...props
}: ProjectsListProps) => {
  const rootRef = useRef<HTMLDivElement>(null!)
  const desktopFiltersRef = useRef<HTMLDivElement>(null!)
  const desktopOverlayRef = useRef<HTMLDivElement>(null!)
  const mobileOverlayRef = useRef<HTMLDivElement>(null!)
  const firstAnimationRef = useRef(true)
  const { strings } = useStore()
  const isSmall = useMedia('(max-width: 767px)', false)

  const projectsListData = useMemo(
    () => pageProps.projects_list || {},
    [pageProps.projects_list]
  )
  const title = projectsListData.title || ''
  const projects = useMemo(
    () => projectsListData.collection || [],
    [projectsListData.collection]
  )

  const [selectedFilters, setSelectedFilters] =
    useState<Record<FilterKey, string[]>>(createEmptyFilters())
  const [draftFilters, setDraftFilters] =
    useState<Record<FilterKey, string[]>>(createEmptyFilters())
  const [desktopFiltersOpen, setDesktopFiltersOpen] = useState(false)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [portalReady, setPortalReady] = useState(false)

  const availableValues = useMemo(() => {
    return {
      context: Array.from(
        new Set(projects.map((item) => item.context).filter(Boolean))
      ),
      style: Array.from(
        new Set(projects.map((item) => item.style).filter(Boolean))
      ),
      material: Array.from(
        new Set(projects.map((item) => item.material).filter(Boolean))
      ),
      country: Array.from(
        new Set(projects.map((item) => item.country).filter(Boolean))
      ),
      year: Array.from(
        new Set(projects.map((item) => String(item.year || '')).filter(Boolean))
      )
    }
  }, [projects])

  const filters = useMemo(
    () => [
      /* {
        key: 'context' as const,
        label: strings?.labels?.projects_filter_context || 'Contesto',
        options: sortByOrder(availableValues.context, CONTEXT_ORDER)
      },
      {
        key: 'style' as const,
        label: strings?.labels?.projects_filter_style || 'Stile',
        options: sortByOrder(availableValues.style, STYLE_ORDER)
      },
      {
        key: 'material' as const,
        label: strings?.labels?.projects_filter_material || 'Materiali',
        options: sortByOrder(availableValues.material, MATERIAL_ORDER)
      }, */
      {
        key: 'country' as const,
        label: strings?.labels?.projects_filter_country || 'Paese',
        options: sortByOrder(availableValues.country, COUNTRY_ORDER)
      }
      /* {
        key: 'year' as const,
        label: strings?.labels?.projects_filter_year || 'Anno',
        options: sortByOrder(availableValues.year, YEAR_ORDER)
      } */
    ],
    [availableValues, strings]
  )

  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      return (Object.keys(selectedFilters) as FilterKey[]).every((key) => {
        const selected = selectedFilters[key]
        if (selected.length === 0) return true

        const value =
          key === 'year'
            ? String(project.year || '')
            : String(project[key] || '')

        return selected.includes(value)
      })
    })
  }, [projects, selectedFilters])

  const filteredProjectsKey = useMemo(
    () =>
      filteredProjects
        .map(
          (project) =>
            `${project.slug}-${project.title}-${project.country}-${project.year}`
        )
        .join('|'),
    [filteredProjects]
  )

  const selectedCount = useMemo(
    () =>
      (Object.keys(selectedFilters) as FilterKey[]).reduce(
        (total, key) => total + selectedFilters[key].length,
        0
      ),
    [selectedFilters]
  )

  const draftSelectedCount = useMemo(
    () =>
      (Object.keys(draftFilters) as FilterKey[]).reduce(
        (total, key) => total + draftFilters[key].length,
        0
      ),
    [draftFilters]
  )

  useGSAP(
    () => {
      const cards = gsap.utils.toArray<HTMLElement>(
        '[data-project-card]',
        rootRef.current
      )
      if (!cards.length) return

      if (firstAnimationRef.current) {
        firstAnimationRef.current = false
        gsap.set(cards, { opacity: 1 })
        return
      }

      gsap.fromTo(
        cards,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.35,
          ease: 'power2.out',
          stagger: 0.03
        }
      )
    },
    {
      scope: rootRef,
      dependencies: [filteredProjectsKey]
    }
  )

  useGSAP(
    () => {
      if (!desktopOverlayRef.current) return

      gsap.to(desktopOverlayRef.current, {
        opacity: desktopFiltersOpen ? 1 : 0,
        y: desktopFiltersOpen ? 0 : -12,
        pointerEvents: desktopFiltersOpen ? 'auto' : 'none',
        duration: 0.25,
        ease: 'power2.out'
      })
    },
    {
      dependencies: [desktopFiltersOpen],
      scope: rootRef
    }
  )

  useGSAP(
    () => {
      if (!mobileOverlayRef.current) return

      gsap.to(mobileOverlayRef.current, {
        opacity: mobileFiltersOpen ? 1 : 0,
        y: mobileFiltersOpen ? 0 : 16,
        pointerEvents: mobileFiltersOpen ? 'auto' : 'none',
        duration: 0.25,
        ease: 'power2.out'
      })
    },
    {
      dependencies: [mobileFiltersOpen],
      scope: rootRef
    }
  )

  useEffect(() => {
    if (!isSmall && mobileFiltersOpen) {
      setMobileFiltersOpen(false)
    }
  }, [isSmall, mobileFiltersOpen])

  useEffect(() => {
    setPortalReady(true)
  }, [])

  useEffect(() => {
    if (!mobileFiltersOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileFiltersOpen(false)
      }
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [mobileFiltersOpen])

  useEffect(() => {
    if (!desktopFiltersOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDesktopFiltersOpen(false)
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [desktopFiltersOpen])

  useClickAway(desktopFiltersRef, () => {
    if (!desktopFiltersOpen || isSmall) return
    setDesktopFiltersOpen(false)
  })

  const toggleFilterValue = (
    state: Record<FilterKey, string[]>,
    key: FilterKey,
    value: string
  ) => {
    const selected = state[key]
    const nextSelected = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value]

    return {
      ...state,
      [key]: nextSelected
    }
  }

  const toggleDesktopFilter = (key: FilterKey, value: string) => {
    setSelectedFilters((current) => toggleFilterValue(current, key, value))
  }

  const toggleDraftFilter = (key: FilterKey, value: string) => {
    setDraftFilters((current) => toggleFilterValue(current, key, value))
  }

  const openMobileFilters = () => {
    setDraftFilters(selectedFilters)
    setMobileFiltersOpen(true)
  }

  const applyMobileFilters = () => {
    setSelectedFilters(draftFilters)
    setMobileFiltersOpen(false)
  }

  const getDesktopRowImagesCount = (index: number) => (index % 2 === 0 ? 3 : 4)

  const getProjectPreviewImages = (project: ProjectsListItem) => {
    const previewImages = (project.preview_images || []).filter(
      (image) => image?.url
    )

    if (previewImages.length > 0) return previewImages
    if (project.image?.url) return [project.image]

    return []
  }

  return (
    <Section ref={rootRef} className={cn(className)} {...props}>
      <Spacer variant="xxl" />
      <Container>
        {title ? (
          <Typography variant="h2" component="h1" className="mb-spacing-md">
            {title}
          </Typography>
        ) : null}

        <div
          ref={desktopFiltersRef}
          className="relative mb-spacing-lg hidden md:block"
        >
          <div className="flex items-center gap-spacing-sm py-spacing-xs">
            <button
              type="button"
              aria-expanded={desktopFiltersOpen}
              aria-controls="projects-desktop-filters"
              onClick={() => setDesktopFiltersOpen((state) => !state)}
              className="flex items-center justify-center rounded-radius-minimal border border-border-secondary-light p-spacing-2xs"
            >
              <SVGIcon
                symbol="arrow-down"
                className={cn('size-[1.4rem] transition-transform', {
                  'rotate-180': desktopFiltersOpen
                })}
              />
            </button>

            <div className="grid flex-1 grid-cols-5 gap-spacing-xs">
              {filters.map((filter) => (
                <div
                  key={filter.key}
                  className="flex items-center gap-spacing-xs"
                >
                  <button
                    type="button"
                    onClick={() => setDesktopFiltersOpen((state) => !state)}
                    className="condensed flex items-center gap-[10px] p-spacing-2xs"
                  >
                    <Typography variant="regular">{filter.label}</Typography>
                    {selectedFilters[filter.key].length > 0 ? (
                      <span className="text-small condensed text-trim inline-block rounded-radius-minimal bg-surface-primary-light p-spacing-2xs text-text-heading-invert-light">
                        {selectedFilters[filter.key].length}
                      </span>
                    ) : null}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div
            id="projects-desktop-filters"
            ref={desktopOverlayRef}
            className="pointer-events-none absolute left-0 right-0 top-full z-20 bg-background-frame-light pb-spacing-lg pl-[5.7rem] pt-spacing-xs opacity-0"
          >
            <div className="grid grid-cols-5 gap-spacing-xs">
              {filters.map((filter) => (
                <div key={`desktop-open-${filter.key}`}>
                  <div className="flex flex-col items-start gap-spacing-2xs">
                    {filter.options.map((option) => {
                      const isActive =
                        selectedFilters[filter.key].includes(option)

                      return (
                        <button
                          key={`${filter.key}-${option}`}
                          type="button"
                          aria-pressed={isActive}
                          onClick={() =>
                            toggleDesktopFilter(filter.key, option)
                          }
                          className={cn(
                            'text-small condensed inline-block rounded-radius-minimal p-spacing-2xs transition-colors',
                            isActive
                              ? 'bg-surface-primary-light text-text-primary-invert-light dark:bg-surface-primary-dark dark:text-text-primary-invert-dark'
                              : 'text-black/80 bg-transparent'
                          )}
                        >
                          <span className="text-trim inline-block">
                            {option}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-spacing-md md:hidden">
          <button
            type="button"
            aria-expanded={mobileFiltersOpen}
            aria-controls="projects-mobile-filters"
            onClick={openMobileFilters}
            className="flex min-h-[5.2rem] w-full items-center justify-between rounded-radius-minimal border border-border-secondary-light bg-background-frame-light px-spacing-xs dark:border-border-secondary-dark dark:bg-background-frame-dark"
          >
            <Typography variant="regular" className="condensed">
              {strings?.labels?.projects_filters || 'Filtri'}
            </Typography>
            <div className="flex items-center gap-[8px]">
              {selectedCount > 0 ? (
                <span className="text-small condensed text-trim inline-block rounded-radius-minimal bg-surface-primary-light p-spacing-2xs text-text-heading-invert-light dark:bg-surface-primary-dark dark:text-text-heading-invert-dark">
                  {selectedCount}
                </span>
              ) : null}
              <SVGIcon symbol="arrow-down" className="size-[1.4rem]" />
            </div>
          </button>
        </div>

        {portalReady
          ? createPortal(
              <div
                id="projects-mobile-filters"
                ref={mobileOverlayRef}
                role="dialog"
                aria-modal="true"
                aria-hidden={!mobileFiltersOpen}
                className="pointer-events-none fixed inset-0 z-[41] opacity-0 md:hidden"
              >
                <div className="absolute inset-0 flex flex-col bg-background-frame-light dark:bg-background-frame-dark">
                  <div className="flex items-center justify-between border-b border-border-secondary-light px-[2rem] py-spacing-xs dark:border-border-secondary-dark">
                    <Typography
                      variant="regular"
                      component="h2"
                      className="condensed"
                    >
                      {strings?.labels?.projects_filters || 'Filtri'}
                    </Typography>
                    <button
                      type="button"
                      onClick={() => setMobileFiltersOpen(false)}
                      className="flex items-center justify-center rounded-radius-minimal border border-border-secondary-light p-spacing-2xs dark:border-border-secondary-dark"
                      aria-label={strings?.labels?.close || 'Chiudi filtri'}
                    >
                      <SVGIcon symbol="close" className="size-[1.4rem]" />
                    </button>
                  </div>

                  <div
                    className="flex-1 overflow-y-auto pt-spacing-sm"
                    data-lenis-prevent
                  >
                    <div className="flex flex-col gap-spacing-xs">
                      {filters.map((filter) =>
                        filter.options.length > 0 ? (
                          <div
                            key={`mobile-${filter.key}`}
                            className="border-b border-border-secondary-light px-[1.2rem] pb-spacing-xs [&:last-child]:border-b-0"
                          >
                            <div className="mb-[12px] flex items-center justify-between">
                              <Typography
                                variant="regular"
                                className="condensed flex uppercase"
                              >
                                <span>{filter.label}</span>
                              </Typography>
                              <Typography
                                variant="regular"
                                className="condensed flex uppercase"
                              >
                                {draftFilters[filter.key].length > 0 ? (
                                  <div className="justify-normally flex items-center gap-[2px]">
                                    <span className="text-small condensed text-trim inline-block rounded-radius-minimal bg-surface-primary-light p-spacing-2xs text-text-heading-invert-light dark:bg-surface-primary-dark dark:text-text-heading-invert-dark">
                                      {draftFilters[filter.key].length}
                                    </span>
                                    <button
                                      className="flex items-center justify-center rounded-radius-minimal p-spacing-2xs text-text-primary-light"
                                      onClick={() =>
                                        setDraftFilters((current) => ({
                                          ...current,
                                          [filter.key]: []
                                        }))
                                      }
                                      aria-label={
                                        strings?.labels?.reset_filter ||
                                        'Rimuovi filtro'
                                      }
                                    >
                                      <SVGIcon
                                        symbol="close"
                                        className="size-[1.6rem]"
                                      />
                                    </button>
                                  </div>
                                ) : null}
                              </Typography>
                            </div>
                            <div className="flex flex-col flex-wrap items-start gap-spacing-3xs">
                              {filter.options.map((option) => {
                                const isActive =
                                  draftFilters[filter.key].includes(option)

                                return (
                                  <button
                                    key={`mobile-option-${filter.key}-${option}`}
                                    type="button"
                                    aria-pressed={isActive}
                                    onClick={() =>
                                      toggleDraftFilter(filter.key, option)
                                    }
                                    className={cn(
                                      'text-small condensed block rounded-radius-minimal p-spacing-2xs transition-colors',
                                      isActive
                                        ? 'bg-surface-primary-light text-text-primary-invert-light dark:bg-surface-primary-dark dark:text-text-primary-invert-dark'
                                        : 'text-black/80 bg-transparent'
                                    )}
                                  >
                                    <span className="text-trim inline-block">
                                      {option}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>

                  <div className="border-black/15 dark:border-white/15 border-t p-[2rem]">
                    <button
                      type="button"
                      onClick={applyMobileFilters}
                      className="flex min-h-[5.2rem] w-full items-center justify-center rounded-radius-minimal bg-surface-primary-light px-spacing-xs text-text-primary-invert-light dark:bg-surface-primary-dark dark:text-text-primary-invert-dark"
                    >
                      <Typography variant="regular">
                        {strings?.labels?.apply || 'Applica filtri'}
                        {draftSelectedCount > 0
                          ? ` (${draftSelectedCount})`
                          : ''}
                      </Typography>
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )
          : null}

        {filteredProjects.length === 0 ? (
          <Typography variant="large" paragraph className="mb-spacing-lg">
            {strings?.labels?.projects_no_results || 'Nessun progetto trovato'}
          </Typography>
        ) : null}
      </Container>

      <div className="mx-padding-sm flex flex-col gap-y-[40px]">
        {filteredProjects.map((project, index) => {
          const projectImages = getProjectPreviewImages(project)
          const desktopImagesCount = getDesktopRowImagesCount(index)
          const desktopImages = projectImages.slice(0, desktopImagesCount)
          const leadImage = projectImages[0]

          return (
            <article key={`${project.title}-${index}`} data-project-card>
              <Link
                href={`/projects/${project.slug || 'project'}`}
                className="group/row block"
                scroll={false}
              >
                <div className="md:hidden">
                  <div className="relative aspect-[4/3] overflow-hidden border border-border-secondary-light">
                    {leadImage?.url ? (
                      <img
                        src={leadImage.url}
                        alt={leadImage.alt || project.title}
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </div>
                  <div className="flex flex-col justify-between gap-spacing-2xs border border-t-0 border-border-secondary-light p-spacing-xs">
                    <Typography
                      variant="h5"
                      component="h2"
                      className="text-large"
                    >
                      {project.title}
                    </Typography>
                    <Typography
                      variant="regular"
                      className="text-text-primary-light dark:text-text-primary-dark"
                    >
                      {project.location}, {project.country}
                    </Typography>
                  </div>
                </div>

                <div className="hidden gap-[1px] md:grid md:grid-cols-12">
                  {desktopImages.map((image, imageIndex) => {
                    const isLeadImage = imageIndex === 0

                    return (
                      <div
                        key={`${project.slug}-${imageIndex}`}
                        className={cn(
                          'relative aspect-[4/3] overflow-hidden border border-border-secondary-light',
                          desktopImagesCount === 3
                            ? 'md:col-span-4'
                            : 'md:col-span-3'
                        )}
                      >
                        <img
                          src={image.url}
                          alt={image.alt || project.title}
                          className={cn(
                            'h-full w-full object-cover transition-opacity duration-300',
                            isLeadImage && 'group-hover/row:opacity-0'
                          )}
                        />

                        {isLeadImage ? (
                          <div className="pointer-events-none absolute inset-0 flex flex-col justify-between bg-background-frame-light p-spacing-sm opacity-0 transition-opacity duration-300 group-hover/row:opacity-100 dark:bg-background-frame-dark">
                            <Typography
                              variant="h5"
                              component="h2"
                              className="text-d-h6"
                            >
                              {project.title}
                            </Typography>
                            <Typography
                              variant="regular"
                              className="text-text-primary-light dark:text-text-primary-dark"
                            >
                              {project.location}, {project.country}
                            </Typography>
                          </div>
                        ) : null}
                      </div>
                    )
                  })}
                </div>
              </Link>
            </article>
          )
        })}
      </div>
      <Spacer variant="xxl" />
    </Section>
  )
}

export default ProjectsList
